import {ChangeDetectionStrategy, Component, effect, input, OnChanges} from '@angular/core';
import {ResearchPaperDTO, ResearchSource} from '../../dto/research-paper';
import {KeyValuePipe} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatSuffix} from '@angular/material/form-field';
import {MatTooltip} from '@angular/material/tooltip';
import {
  paperAuditedScreening,
  paperCompleteManuelOverride,
  paperHasManuelAccepted,
  paperPassedScreening,
  paperScreeningResult
} from '../../helper/research-paper';
import {ScreeningSettingsResearchDBDTO} from '../../dto/screening-settings';

@Component({
  selector: 'app-research-prisma-flow',
  imports: [
    KeyValuePipe,
    MatIcon,
    MatSuffix,
    MatTooltip
  ],
  templateUrl: './research-prisma-flow.component.html',
  styleUrl: './research-prisma-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchPrismaFlowComponent implements OnChanges {
  readonly papers = input.required<ResearchPaperDTO[]>();
  readonly queries = input.required<ScreeningSettingsResearchDBDTO[]>();

  allPapersCount = 0;
  screening1PassedCount = 0;
  screening2PassedCount = 0;
  screening1AuditedCount = 0;
  screening2AuditedCount = 0;
  screeningPassedManuel1Count = 0;
  screeningPassedManuel2Count = 0;
  screeningPassedManuel1WithoutAcCount = 0;
  screeningPassedManuel2WithoutAcCount = 0;
  screening1FailedCount = 0;
  screening2FailedCount = 0;
  screening1Passed: { [label: string]: number } = {};
  screening2Passed: { [label: string]: number } = {};
  paperSources: { [label: string]: number } = {};

  ngOnChanges(): void {
    this.allPapersCount = 0;
    this.screening1PassedCount = 0;
    this.screening2PassedCount = 0;
    this.screening1AuditedCount = 0;
    this.screening2AuditedCount = 0;
    this.screening1FailedCount = 0;
    this.screening2FailedCount = 0;
    this.screeningPassedManuel1Count = 0;
    this.screeningPassedManuel2Count = 0;
    this.screeningPassedManuel1WithoutAcCount = 0;
    this.screeningPassedManuel2WithoutAcCount = 0;
    this.screening1Passed = {};
    this.screening2Passed = {};
    this.paperSources = {};
  }

  applyFlow = effect(() => {
    const papers = this.papers();
    this.allPapersCount = papers.length;
    this.screeningPassedManuel1Count = papers.filter(paper => paperCompleteManuelOverride(paper)).length;
    this.screeningPassedManuel2Count = papers.filter(paper => paperCompleteManuelOverride(paper, true)).length;
    this.screeningPassedManuel1WithoutAcCount = papers.filter(paper => paperHasManuelAccepted(paper)).length;
    this.screeningPassedManuel2WithoutAcCount = papers.filter(paper => paperHasManuelAccepted(paper, true)).length;
    this.screening1AuditedCount = papers.filter(paper => paperAuditedScreening(paper)).length;
    this.screening2AuditedCount = papers.filter(paper => paperAuditedScreening(paper, true)).length;

    papers.filter(
      paper => !!paper.source
    ).forEach(paper => {
      const sources = this.getPapersSources(paper);
      for (const source of sources) {
        if (!this.paperSources[source]) {
          this.paperSources[source] = 0;
        }
        this.paperSources[source]++;
      }

      //SCREENING 1
      if (paperScreeningResult(paper).length) {
        paperScreeningResult(paper).filter(
          result => !result.passed
        ).forEach(result => {
          if (!this.screening1Passed[result.criteria]) {
            this.screening1Passed[result.criteria] = 0;
          }
          this.screening1Passed[result.criteria]++;
        });
        if (paperAuditedScreening(paper) && !paperPassedScreening(paper) && !paperHasManuelAccepted(paper)) {
          this.screening1FailedCount++;
        }
      }
      if (paperPassedScreening(paper) || paperHasManuelAccepted(paper)) {
        if (paperPassedScreening(paper)) {
          this.screening1PassedCount++;
        }

        //SCREENING TWO
        if (paperPassedScreening(paper, true)) {
          this.screening2PassedCount++;
        } else if (paperHasManuelAccepted(paper)) {
          this.screening2FailedCount++;
        }
        if (!paper.hasDownloadedPdf) {
          if (!this.screening2Passed["No full text access"]) {
            this.screening2Passed["No full text access"] = 0;
          }
          this.screening2Passed["No full text access"]++;
          this.screening2FailedCount++;
          this.screening2AuditedCount++;
        }
        if (!paperScreeningResult(paper, true).length) {
          return;
        }

        paperScreeningResult(paper, true).filter(
          result => !result.passed
        ).forEach(result => {
          if (!this.screening2Passed[result.criteria]) {
            this.screening2Passed[result.criteria] = 0;
          }
          this.screening2Passed[result.criteria]++;
        });
      }
    });
  });

  getPapersSources(paper: ResearchPaperDTO): string[] {
    if (paper.source && paper.source === ResearchSource.SELF_CREATED) {
      return [ResearchSource.SELF_CREATED];
    }
    const queryIds = paper.queryIds;
    if (!queryIds || !queryIds.length) {
      return [];
    }
    return this.queries().filter(query => queryIds.includes(query.id)).map(query => query.source);
  }

  get screeningPassedTotalCount(): number {
    return this.screening2PassedCount + this.screeningPassedManuel2WithoutAcCount;
  }

  get manualSources(): number {
    return this.paperSources['SELF_CREATED'] ?? 0;
  }

  get externalSourcesCount(): number {
    return Object.keys(this.paperSources)
      .filter(key => key !== 'SELF_CREATED')
      .reduce((sum, key) => sum + this.paperSources[key], 0);

  }
}
